import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { upperFirst } from 'lodash'
import { BlocksIcon, DiamondPlusIcon, FileCode2Icon, LayersIcon, Save, SquareArrowOutUpRight, LoaderCircle, RefreshCw, RefreshCwOff } from 'lucide-react'
import { Button, cn, DefaultTooltip, InputGroup, InputGroupText } from 'twyne-new.ui'

import { useAppAlert } from '@/components/AppAlert'
import { useBuilderStore } from '@/components/builder/utils/store'
import { useGlobalTailwindConfig, useMetadata } from '@/components/builder/utils/tailwindConfig/hooks'
import { DefaultChangeHistory, DefaultChangeHistoryHeader } from '@/components/change-history'
import { DefaultVersion, DefaultVersionHeader } from '@/components/version'
import { useBuilder, useBuilderContext, useLeftSidebar, useAutoSave } from '@/modules/cms/views/post/components/builder/context'
import routeStore from 'modules/cms/stores/route'
import appStore from 'stores/app';

import { usePostStore } from '../../../store'
import BuilderConfig from '../../BuilderConfig'
import { useFormMetaParams } from '../hooks/useMetadata'

export default function Header({ page = <PageButton />, layers = <LayersButton />, assets = <AssetsButton />, blocks = <BlocksButton />, title = <ThemeName />, versionStatus = <VersionStatus /> }) {
  return <>
    <div className='p-4 twy-bg-base-accent border-b twy-border-base flex justify-between items-center'>
      <div className='flex gap-4'>
        {page}
        {assets}
        {layers}
        {blocks}
        {versionStatus}
      </div>
      {title}
      <div className='flex gap-2'>
        <SaveStatus />
        <ChangeHistoryStatus />
        <Actions />
        <BuilderConfig />
        <Version />
        <ChangeHistory />
      </div>
    </div>
  </>
}

export function PageButton() {
  return <HeaderButton name='page'>
    <FileCode2Icon size={16} />
    Pages
  </HeaderButton>
}

export function LayersButton() {
  return <HeaderButton name='layers'>
    <LayersIcon size={16} />
    Layers
  </HeaderButton>
}

export function AssetsButton() {
  return <HeaderButton name='assets'>
    <DiamondPlusIcon size={16} />
    Assets
  </HeaderButton>
}

export function BlocksButton() {
  return <HeaderButton name='blocks'>
    <BlocksIcon size={16} />
    Blocks
  </HeaderButton>
}

function ThemeName() {
  const { theme: { name } } = useBuilderContext()

  return <div className='flex gap-2 text-[14px]'>
    <div>Themes</div>
    <div>/</div>
    <div className='twy-text-base-accent'>{name.value}</div>
  </div>
}

function HeaderButton({ children, name }) {
  const leftSidebar = useLeftSidebar()

  const handleClick = () => {
    leftSidebar.setValue(oldValue => oldValue !== name ? name : undefined)
  }

  const active = leftSidebar.value === name

  const variant = active ? 'light' : 'ghost'
  const color = active ? 'accent' : 'neutral'

  return <Button variant={variant} color={color} onClick={handleClick}>
    {children}
  </Button>
}

function SaveStatus() {
  const { saved } = useAutoSave()

  if (saved) {
    return;
  }

  return <span className='p-2'>
    unsaved changes
  </span>
}

function useSitePath() {
  const [sitePath, setSitePath] = useState()
  const { collection: { data } } = usePostStore()
  const { postBuilder: { version: { item: version } } } = useBuilderStore()
  const post_id = data?.id
  const handleAddFormParam = useFormMetaParams()

  useEffect(() => {
    !async function loadRoute() {
      const routes = await routeStore.all(appStore.site.id).then(res => res.data && res.data.rows);
      const route = routes
        .find(d => d.entity_id === post_id)

      let sitePath = null
      if (route) {
        const url = new URL(`https://${appStore.site.domain}${route.path}`)
        url.searchParams.set('nocache', 'true')
        url.searchParams.set('force_version', 'true')
        version?.slug && url.searchParams.set('__version', version.slug)
        handleAddFormParam(url.searchParams)
        if (process.env.NODE_ENV === 'development') {
          url.port = 3002
          url.protocol = 'http'
        }
        sitePath = url.href
      }

      setSitePath(sitePath)
    }()
  }, [post_id, handleAddFormParam, version])

  return sitePath
}

function ChangeHistory() {
  const { postBuilder, loading } = useBuilderStore()
  return <DefaultChangeHistory entityName="cms_post_builders" entityCollection={postBuilder} loading={loading} />
}

function ChangeHistoryStatus() {
  const { postBuilder, loading } = useBuilderStore()
  return <DefaultChangeHistoryHeader entityCollection={postBuilder} loading={loading} />
}

function Version() {
  const { postBuilder, loading, postBuilder: { history: { preview } } } = useBuilderStore()

  if (preview) {
    return null;
  }

  return <DefaultVersion entityName="cms_post_builders" entityCollection={postBuilder} loading={loading} />
}

function VersionStatus() {
  const { postBuilder, loading } = useBuilderStore()
  return <DefaultVersionHeader entityCollection={postBuilder} loading={loading} />
}

function AutoSaveButton() {
  const { toggleAutoSave, disableAutoSave } = useBuilderStore()

  return <Button color='accent' variant='outline' onClick={toggleAutoSave}>
    <DefaultTooltip content={disableAutoSave ? 'Enable auto-save' : 'Disable auto-save'}>
      {disableAutoSave ? <RefreshCwOff size={16} /> : <RefreshCw size={16} />}
    </DefaultTooltip>
  </Button>
}

function Actions() {
  const { postBuilder: { history: { preview } }, loading: storeLoading } = useBuilderStore()
  const { content_type: contentType } = useParams()
  const { handleSave: onSave, handlePublish: onPublish } = useBuilder()
  const { handleSuccess, handleError } = useAppAlert()
  const tailwindConfig = useGlobalTailwindConfig()
  const { value: meta } = useMetadata()

  const [loading, setLoading] = useState('')
  const sitePath = useSitePath()
  const [popup, setPopup] = useState(false)

  const entity = useMemo(() => upperFirst(contentType), [contentType])
  const handleOpen = useCallback(() => window.open(sitePath, 'preview'), [sitePath])

  const handleSave = useCallback(async () => {
    setLoading('saving')
    try {
      await onSave()
      handleSuccess('Success', `${entity} saved.`)
    } catch (error) {
      handleError('Error', `${entity} was not saved.`)
    }
    setLoading('')
  }, [onSave, entity, handleSuccess, handleError])

  const handlePublish = useCallback(async () => {
    setLoading('publishing')
    try {
      await onPublish({ tailwindConfig, meta })
      handleSuccess('Success', `${entity} published.`)

      setPopup(popoup => {
        if (popoup && !popoup.closed) {
          popoup.location.replace(sitePath)
          popoup.focus()
          return popoup
        } else {
          return handleOpen()
        }
      })
    } catch (error) {
      handleError('Error', `${entity} was not published.`)
    }
    setLoading('')
  }, [onPublish, entity, handleSuccess, handleError, tailwindConfig, meta, sitePath, handleOpen])

  if (preview) {
    return null;
  }

  return <>
    <InputGroup button>
      <InputGroupText addonType='prepend'>
        <DefaultTooltip content='open page' >
          <Button color='accent' onClick={handleOpen} className='p-2'>
            <SquareArrowOutUpRight size={16} />
          </Button>
        </DefaultTooltip>
      </InputGroupText>
      <InputGroupText addonType='append'>
        <Button className={cn("items-center relative", loading === 'publishing' && 'pl-6')} color='accent' onClick={handlePublish} disabled={!!loading || !!storeLoading}>
          {loading === 'publishing' && <LoaderCircle className="animate-spin absolute top-1/2 left-1 -translate-y-1/2" size={16} />}
          Publish
        </Button>
      </InputGroupText>
    </InputGroup>
    <InputGroup button>
      <InputGroupText addonType='prepend'>
        <Button className="items-center" color='accent' variant='outline' onClick={handleSave} disabled={!!loading || !!storeLoading}>
          {loading === 'saving' ? <LoaderCircle className="animate-spin" size={16} /> : <Save size={16} />}
          Save
        </Button>
      </InputGroupText>
      <InputGroupText addonType='append'>
        <AutoSaveButton />
      </InputGroupText>
    </InputGroup>
  </>
}
